import React, { useState, useEffect, useMemo } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Users,
  UserPlus,
  Crown,
  Search,
  Loader2,
  X,
  ChevronRight,
  LogOut,
} from 'lucide-react';
import { messagingApi } from '@/api/messaging';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { getDisplayName } from '@shared/utils/display-name';

interface GroupMember {
  entity_id: string;
  entity_role: 'client' | 'filmmaker';
  role: 'admin' | 'member';
  name: string;
  avatar?: string;
  is_active: boolean;
}

interface GroupDetails {
  room_name: string;
  name: string;
  room_type: 'direct' | 'group';
  description?: string;
  avatar_url?: string;
  participants: GroupMember[];
}

interface UserSearchResult {
  id: string;
  name: string;
  avatar_url?: string;
  role: 'client' | 'filmmaker';
}

interface GroupInfoSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roomName: string;
  currentEntityId: string;
  currentEntityRole: 'client' | 'filmmaker';
  onViewProfile?: (id: string, role: 'client' | 'filmmaker') => void;
  onLeaveGroup?: (roomName: string) => void;
}

export function GroupInfoSheet({
  open,
  onOpenChange,
  roomName,
  currentEntityId,
  currentEntityRole,
  onViewProfile,
  onLeaveGroup,
}: GroupInfoSheetProps) {
  const [groupDetails, setGroupDetails] = useState<GroupDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showAddMembers, setShowAddMembers] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [allUsers, setAllUsers] = useState<UserSearchResult[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<UserSearchResult[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isInviting, setIsInviting] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  // Check if current user is admin
  const isAdmin = useMemo(() => {
    if (!groupDetails) return false;
    const currentParticipant = groupDetails.participants.find(
      p => p.entity_id === currentEntityId && p.entity_role === currentEntityRole
    );
    return currentParticipant?.role === 'admin';
  }, [groupDetails, currentEntityId, currentEntityRole]);

  // Active members count
  const activeMemberCount = useMemo(() => {
    return groupDetails?.participants.filter(p => p.is_active).length || 0;
  }, [groupDetails]);

  // Fetch group details when sheet opens
  useEffect(() => {
    if (open && roomName) {
      fetchGroupDetails();
    }
  }, [open, roomName]);

  const fetchGroupDetails = async () => {
    setIsLoading(true);
    try {
      const details = await messagingApi.getRoomDetails(roomName);
      setGroupDetails(details);
    } catch (err) {
      console.error('Error fetching group details:', err);
      toast.error('Failed to load group details');
    } finally {
      setIsLoading(false);
    }
  };

  const loadAllUsers = async () => {
    setIsLoadingUsers(true);
    try {
      const [filmmakersResponse, clientsResponse] = await Promise.all([
        messagingApi.getAllFilmmakers(),
        messagingApi.getAllClients(),
      ]);

      const filmmakers = Array.isArray(filmmakersResponse) ? filmmakersResponse : [];
      const clients = Array.isArray(clientsResponse) ? clientsResponse : [];

      // Get existing member IDs
      const existingMemberIds = new Set(
        groupDetails?.participants.map(p => p.entity_id) || []
      );

      // Format and filter out existing members - use shared getDisplayName for privacy
      const formattedFilmmakers: UserSearchResult[] = filmmakers
        .filter(f => f.verified && f.setup && !existingMemberIds.has(f.filmmaker_id))
        .map(f => ({
          id: f.filmmaker_id,
          name: getDisplayName(f, 'Filmmaker'),
          avatar_url: f.profile_pic,
          role: 'filmmaker' as const,
        }));

      const formattedClients: UserSearchResult[] = clients
        .filter(c => c.verified && c.setup && !existingMemberIds.has(c.client_id))
        .map(c => {
          let logoUrl = c.logo_url || c.profile_pic;
          if (logoUrl && !logoUrl.startsWith('http')) {
            const { data } = supabase.storage.from('public-assets').getPublicUrl(logoUrl);
            logoUrl = data.publicUrl;
          }
          return {
            id: c.client_id,
            name: getDisplayName(c, 'Client'),
            avatar_url: logoUrl,
            role: 'client' as const,
          };
        });

      setAllUsers([...formattedFilmmakers, ...formattedClients].sort((a, b) =>
        a.name.localeCompare(b.name)
      ));
    } catch (err) {
      console.error('Error loading users:', err);
      toast.error('Failed to load users');
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const handleShowAddMembers = () => {
    setShowAddMembers(true);
    setSelectedUsers([]);
    setSearchTerm('');
    loadAllUsers();
  };

  const filteredUsers = useMemo(() => {
    if (searchTerm.trim() === '') return allUsers;
    const lowercaseSearch = searchTerm.toLowerCase();
    return allUsers.filter(user => user.name.toLowerCase().includes(lowercaseSearch));
  }, [searchTerm, allUsers]);

  const toggleUserSelection = (user: UserSearchResult) => {
    setSelectedUsers(prev => {
      const isSelected = prev.some(u => u.id === user.id);
      if (isSelected) {
        return prev.filter(u => u.id !== user.id);
      }
      return [...prev, user];
    });
  };

  const handleInviteMembers = async () => {
    if (selectedUsers.length === 0) return;

    setIsInviting(true);
    try {
      // Send invites to all selected users
      await Promise.all(
        selectedUsers.map(user =>
          messagingApi.sendGroupInvite({
            roomName,
            entityId: user.id,
            entityRole: user.role,
            invitedByEntityId: currentEntityId,
            invitedByEntityRole: currentEntityRole,
          })
        )
      );

      toast.success(`Invites sent to ${selectedUsers.length} user(s)`);
      setShowAddMembers(false);
      setSelectedUsers([]);
      // Refresh group details
      fetchGroupDetails();
    } catch (err: any) {
      console.error('Error inviting members:', err);
      toast.error(err.message || 'Failed to send invites');
    } finally {
      setIsInviting(false);
    }
  };

  // Handle leaving the group
  const handleLeaveGroup = async () => {
    if (!roomName || !currentEntityId) return;

    setIsLeaving(true);
    try {
      await messagingApi.leaveGroupChat(roomName, currentEntityId, currentEntityRole);
      toast.success('You have left the group');
      onOpenChange(false);
      onLeaveGroup?.(roomName);
    } catch (err: any) {
      console.error('Error leaving group:', err);
      toast.error(err.message || 'Failed to leave group');
    } finally {
      setIsLeaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md p-0">
        {showAddMembers ? (
          // Add Members View
          <>
            <SheetHeader className="p-4 border-b">
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowAddMembers(false)}
                  className="h-8 w-8"
                >
                  <X className="h-4 w-4" />
                </Button>
                <div className="flex-1">
                  <SheetTitle>Add Members</SheetTitle>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {selectedUsers.length > 0 ? `${selectedUsers.length} selected` : 'Select users to invite'}
                  </p>
                </div>
                <Button
                  size="sm"
                  disabled={selectedUsers.length === 0 || isInviting}
                  onClick={handleInviteMembers}
                >
                  {isInviting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Invite'}
                </Button>
              </div>
            </SheetHeader>

            {/* Selected users chips */}
            {selectedUsers.length > 0 && (
              <div className="flex flex-wrap gap-2 p-3 border-b bg-muted/30">
                {selectedUsers.map(user => (
                  <Badge
                    key={user.id}
                    variant="secondary"
                    className="pl-1 pr-2 py-1 gap-1.5 cursor-pointer hover:bg-destructive/10"
                    onClick={() => toggleUserSelection(user)}
                  >
                    <Avatar className="h-5 w-5">
                      {user.avatar_url ? (
                        <AvatarImage src={user.avatar_url} alt={user.name} />
                      ) : (
                        <AvatarFallback className="text-[10px]">
                          {user.name.charAt(0)}
                        </AvatarFallback>
                      )}
                    </Avatar>
                    <span className="max-w-[80px] truncate text-xs">{user.name.split(' ')[0]}</span>
                    <X className="h-3 w-3 text-muted-foreground" />
                  </Badge>
                ))}
              </div>
            )}

            {/* Search */}
            <div className="p-3 border-b">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            {/* User list */}
            <ScrollArea className="flex-1 h-[calc(100vh-220px)]">
              {isLoadingUsers ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <p className="mt-3 text-sm text-muted-foreground">Loading users...</p>
                </div>
              ) : filteredUsers.length > 0 ? (
                <div className="p-2">
                  {filteredUsers.map(user => {
                    const isSelected = selectedUsers.some(u => u.id === user.id);
                    return (
                      <div
                        key={user.id}
                        className={cn(
                          "flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors",
                          isSelected ? "bg-primary/10" : "hover:bg-muted/50"
                        )}
                        onClick={() => toggleUserSelection(user)}
                      >
                        <Checkbox checked={isSelected} className="pointer-events-none" />
                        <Avatar className="h-10 w-10">
                          {user.avatar_url ? (
                            <AvatarImage src={user.avatar_url} alt={user.name} />
                          ) : (
                            <AvatarFallback>{user.name.charAt(0).toUpperCase()}</AvatarFallback>
                          )}
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{user.name}</p>
                          <p className="text-xs text-muted-foreground capitalize">{user.role}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12">
                  <Users className="h-10 w-10 text-muted-foreground" />
                  <p className="mt-3 text-sm text-muted-foreground">No users found</p>
                </div>
              )}
            </ScrollArea>
          </>
        ) : (
          // Group Info View
          <>
            <SheetHeader className="p-4 border-b">
              <SheetTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Group Info
              </SheetTitle>
            </SheetHeader>

            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <p className="mt-3 text-sm text-muted-foreground">Loading group info...</p>
              </div>
            ) : groupDetails ? (
              <ScrollArea className="h-[calc(100vh-80px)]">
                {/* Group Header */}
                <div className="p-6 flex flex-col items-center border-b">
                  <Avatar className="h-20 w-20 mb-3">
                    {groupDetails.avatar_url ? (
                      <AvatarImage src={groupDetails.avatar_url} alt={groupDetails.name} />
                    ) : (
                      <AvatarFallback className="bg-primary text-primary-foreground text-2xl">
                        <Users className="h-10 w-10" />
                      </AvatarFallback>
                    )}
                  </Avatar>
                  <h3 className="text-lg font-semibold">{groupDetails.name}</h3>
                  {groupDetails.description && (
                    <p className="text-sm text-muted-foreground text-center mt-1">
                      {groupDetails.description}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-2">
                    {activeMemberCount} member{activeMemberCount !== 1 ? 's' : ''}
                  </p>
                </div>

                {/* Members Section */}
                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                      Members
                    </h4>
                    {isAdmin && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleShowAddMembers}
                        className="h-8 text-primary"
                      >
                        <UserPlus className="h-4 w-4 mr-1" />
                        Add
                      </Button>
                    )}
                  </div>

                  <div className="space-y-1">
                    {groupDetails.participants
                      .filter(p => p.is_active)
                      .sort((a, b) => {
                        // Admins first, then alphabetically
                        if (a.role === 'admin' && b.role !== 'admin') return -1;
                        if (a.role !== 'admin' && b.role === 'admin') return 1;
                        return a.name.localeCompare(b.name);
                      })
                      .map(member => {
                        const isCurrentUser =
                          member.entity_id === currentEntityId &&
                          member.entity_role === currentEntityRole;

                        return (
                          <div
                            key={`${member.entity_id}-${member.entity_role}`}
                            className={cn(
                              "flex items-center gap-3 p-3 rounded-lg transition-colors",
                              onViewProfile && !isCurrentUser
                                ? "cursor-pointer hover:bg-muted/50"
                                : ""
                            )}
                            onClick={() => {
                              if (onViewProfile && !isCurrentUser) {
                                onViewProfile(member.entity_id, member.entity_role);
                              }
                            }}
                          >
                            <Avatar className="h-10 w-10">
                              {member.avatar ? (
                                <AvatarImage src={member.avatar} alt={member.name} />
                              ) : (
                                <AvatarFallback>
                                  {member.name.charAt(0).toUpperCase()}
                                </AvatarFallback>
                              )}
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-sm truncate">
                                  {member.name}
                                  {isCurrentUser && (
                                    <span className="text-muted-foreground"> (You)</span>
                                  )}
                                </p>
                                {member.role === 'admin' && (
                                  <Badge
                                    variant="secondary"
                                    className="h-5 px-1.5 text-[10px] bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300"
                                  >
                                    <Crown className="h-3 w-3 mr-0.5" />
                                    Admin
                                  </Badge>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground capitalize">
                                {member.entity_role}
                              </p>
                            </div>
                            {onViewProfile && !isCurrentUser && (
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            )}
                          </div>
                        );
                      })}
                  </div>
                </div>

                {/* Leave Group Button */}
                <div className="p-4 border-t">
                  <Button
                    variant="outline"
                    className="w-full text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={handleLeaveGroup}
                    disabled={isLeaving}
                  >
                    {isLeaving ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <LogOut className="h-4 w-4 mr-2" />
                    )}
                    Leave Group
                  </Button>
                </div>
              </ScrollArea>
            ) : (
              <div className="flex flex-col items-center justify-center py-12">
                <Users className="h-10 w-10 text-muted-foreground" />
                <p className="mt-3 text-sm text-muted-foreground">Failed to load group info</p>
              </div>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
